/** The arithmetic behind an operator's card on the campaign Operators tab.
 *
 *  Two percentages live here and they answer different questions:
 *
 *    share      how much of THIS CAMPAIGN'S work sits with this operator
 *    completion how much of THEIR OWN work they have already done
 *
 *  Keeping them named and together is deliberate. The card previously
 *  drew a bar filled to `completion` and printed `share` beside it, so
 *  the busiest operator (98% of both) looked right while everyone else
 *  was mislabelled.
 */
export type OperatorFigures = {
  /** Performed and closed. Nothing else. */
  done: number;
  /** Has a slot, still ahead. */
  upcoming: number;
  /** Invited, no slot chosen yet — the state that used to be counted
   *  as "done" because NULL is not >= now. */
  awaiting: number;
  /** Slot came and went unused. */
  missed: number;
  completionPct: number;
  sharePct: number;
};

export type OperatorCounts = {
  booked: number;
  upcoming: number;
  done?: number;
  live?: number;
  missed?: number;
  awaiting?: number;
};

export function operatorFigures(
  op: OperatorCounts,
  totalBooked: number,
): OperatorFigures {
  // Read, never derived.
  //
  // This used to be `booked - upcoming`, which assumes a session is
  // either ahead of us or finished. Reported 2026-09-28: an operator
  // card read "475 DONE" while the chart beside it said 35 inspections
  // had closed — 461 of that 475 were blank booking shells with
  // `scheduled_for = NULL`, and NULL is not >= now, so every one of
  // them landed in "done".
  const done = op.done ?? 0;
  const upcoming = op.upcoming ?? 0;
  const awaiting = op.awaiting ?? 0;
  const missed = op.missed ?? 0;

  // Completion is against work that actually reached a slot. Counting
  // it against everything assigned would drag it toward zero on a
  // campaign whose invitations are still out — punishing an operator
  // for beneficiaries who have not replied.
  const reached = done + missed + upcoming + (op.live ?? 0);
  return {
    done,
    upcoming,
    awaiting,
    missed,
    completionPct: reached > 0 ? Math.round((done / reached) * 100) : 0,
    sharePct:
      totalBooked > 0 ? Math.round((op.booked / totalBooked) * 100) : 0,
  };
}

/** Initials for the avatar; "?" rather than an empty circle. */
export function operatorInitials(displayName: string): string {
  return (
    displayName
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0])
      .join("") || "?"
  );
}
